import diff from "diff-arrays-of-objects";
import {t} from '@lingui/macro';
import React from 'react';
import FormatNumber from "bukku/components/FormatNumber";
import {Type} from "bukku/redux/Transaction/helper";
import {AuditAction, DiffResultKey} from "../enums";
import AuditInput from "./Components/AuditInput";
import AuditInputDiffDMP from "./Components/DiffDMP";
import AuditInputDiffJs from "./Components/DiffJs";
import AuditCheckboxGroup from "./Components/AuditCheckboxGroup";
import _ from "lodash";

export const isVoid = transactionCompare => transactionCompare.action === AuditAction.MARKED_VOID;

export const titleCase = string => string[0].toUpperCase() + string.slice(1).toLowerCase();

export const getItemByRecordId = (trxArray, recordId) => {
    if (!trxArray)
        return null;
    return trxArray.find(item => item.id === recordId);
};

// inputs
// transactionCompare: { before: { }, after: { } }
// array_key: "form_items", "link_items", "files", "term_items"
// return ==> {
//     'added': [{ ...}],
//     'removed': [{ ...}],
//     'updated': [{ ...}],
//     'same': [{...}]
// }
export const getDiffResult = (transactionCompare, array_key) => diff(
    transactionCompare.before[array_key], //before
    transactionCompare.after[array_key], //after
);

// find the correspondinng object key ('added', 'removed', 'updated', 'same') by providing value
export const getKeyByValue = (object, value) => Object.keys(object)
    .find(key => object[key].indexOf(value) !== -1);

// return: [Object]
// 1. appends a new key-value of 'diff' : ('added'/'removed'/'updated'/same') to each object in the array,
// 2. appends removed objects from (before) to the END of array of (after)
//
// inputs
// transactionCompare: {before:{}, after:{}}
// array_key: "form_items", "link_items", "files", "term_items"
export const preprocessTableData = (transactionCompare, array_key) => {
    const diffResult = getDiffResult(transactionCompare, array_key);
    let mergedData;
    if (transactionCompare.after[array_key]) {
        mergedData = [...transactionCompare.after[array_key], ...diffResult[DiffResultKey.removed]];

        // add a new key 'diff' to indicate change
        mergedData = mergedData.map(item => ({
            ...item,
            diff: getKeyByValue(diffResult, item),
            key: item.id,
        }));

        return mergedData;
    }

    // add a new key 'diff' to indicate change
    mergedData = diffResult[DiffResultKey.removed].map(item => ({
        ...item,
        diff: getKeyByValue(diffResult, item),
        key: item.id,
    }));

    return mergedData;
};

export const getRowClassName = record => {
    // addition of new items
    // if (afterItemsLength > beforeItemsLength && index >= beforeItemsLength)
    if (record.diff === DiffResultKey.added)
        return "audit-view-row row-added";
    // deleted items
    // else if (beforeItemsLength > afterItemsLength && index >= afterItemsLength)
    if (record.diff === DiffResultKey.removed)
        return "audit-view-row row-removed";
    // UNCHANGED or UPDATED rows - no backgroundColor
    return "audit-view-row";
};

export const renderChangeIndicator = (text, record) => {
    if (record.diff === DiffResultKey.added) {
        return (
            <div
                style={{ color: 'green', fontWeight: 500, textAlign: 'center', verticalAlign: 'center' }}
            >
                +
            </div>
        );
    }
    // return <PlusCircleOutlined style={{color:'green'}}/>
    if (record.diff === DiffResultKey.removed) {
        return (
            <div
                style={{ color: 'red', fontWeight: 500, textAlign: 'center', verticalAlign: 'center' }}
            >
                -
            </div>
        );
    }
    // return <MinusCircleOutlined style={{color:'red'}}
    return null;
};

export const renderCellText = (text, record, itemBefore, objectKey, diff_mode) => {
    const divDOM = record[objectKey] || '-';

    let AuditInputDOM;
    switch (diff_mode) {
        case 'dmp':
            AuditInputDOM = (
                <AuditInputDiffDMP
                    before={itemBefore ? itemBefore[objectKey] || '-' : null}
                    after={record[objectKey] || '-'}
                    bordered={false}
                    // maxRows={6}
                    cleanUpMode="efficiency"
                    editCost={10}
                    splitDiff
                />
            );
            break;
        case 'js':
            AuditInputDOM = (
                <AuditInputDiffJs
                    before={itemBefore ? itemBefore[objectKey] || '-' : null}
                    after={record[objectKey] || '-'}
                    bordered={false}
                    // maxRows={6}
                    mode="words"
                    splitDiff
                />
            );
            break;
        default:
            AuditInputDOM = (
                <AuditInput
                    before={itemBefore ? itemBefore[objectKey] || '-' : null}
                    after={record[objectKey] || '-'}
                    // placeholder={"-"}
                    bordered={false}
                    packedRows
                />
            );
            break;
    }

    return record.diff === DiffResultKey.updated ? AuditInputDOM : divDOM;
};

// eslint-disable-next-line default-param-last
export const renderCellFormatNumber = (text, record, itemBefore, objectKey,
    dp = 2, dp2 = undefined, overrideStatus = undefined) => {
    const divDOM = (
        <FormatNumber amount={text} dp={dp} dp2={dp2} />
    );

    const AuditInputDOM = (
        <AuditInput
            before={itemBefore ? <FormatNumber amount={itemBefore[objectKey]} dp={dp} dp2={dp2} /> : null}
            after={<FormatNumber amount={text} dp={dp} dp2={dp2} />}
            bordered={false}
            packedRows
            overrideStatus={overrideStatus}
        />
    );

    return record.diff === DiffResultKey.updated ? AuditInputDOM : divDOM;
};

/**
 *
 * @param {*} text
 * @param {*} record
 * @param {*} itemBefore
 * @param {*} checkboxOptions [] of { value, label }
 * @returns
 */
export const renderCellCheckboxGroup = (text, record = {}, itemBefore = {},
    checkboxOptions = [], disabled = false, processedProductAudit = {}) => {
    const isSellingBefore = processedProductAudit.before?.is_selling;
    const isSellingAfter = processedProductAudit.after?.is_selling;

    const isBuyingBefore = processedProductAudit.before?.is_buying;
    const isBuyingAfter = processedProductAudit.after?.is_buying;

    const allKeys = checkboxOptions.map(option => option.value);

    const defaultBefore = {},
        defaultAfter = {};

    allKeys.forEach(key => {
        defaultBefore[key] = itemBefore[key];
        defaultAfter[key] = record[key];
    });

    // convert object of boolean to array of keys with true
    const checkboxBefore = _.keys(_.pickBy(defaultBefore));
    let checkboxAfter = _.keys(_.pickBy(defaultAfter));

    // Only for product audit UOM table:
    // if previous has default sale/purchase price but after that don't have anymore (due to no more selling/buying), treat it as removed.
    if (checkboxAfter.includes('is_selling_default') && (isSellingBefore && !isSellingAfter))
        checkboxAfter = checkboxAfter.filter(val => val !== 'is_selling_default');
    if (checkboxAfter.includes('is_purchase_default') && (isBuyingBefore && !isBuyingAfter))
        checkboxAfter = checkboxAfter.filter(val => val !== 'is_purchase_default');

    const AuditCheckboxDOM = (
        <AuditCheckboxGroup
            style={{marginTop: 0}}
            valueBefore={checkboxBefore}
            valueAfter={checkboxAfter}
            checkboxOptions={checkboxOptions}
            disabled={disabled}
        />
    );

    return AuditCheckboxDOM;
};

export const renderLocation = (text, record, itemBefore, objectKey) => {
    const divDOM = (
        <strong>{text}</strong>
    );

    const AuditInputDOM = (
        <AuditInput
            before={<strong>{itemBefore ? itemBefore[objectKey] : null}</strong>}
            after={<strong>{text}</strong>}
            placeholder="-"
            bordered={false}
            packedRows
        />
    );

    return record.diff === DiffResultKey.updated ? AuditInputDOM : divDOM;
};

export const getContactItemLabel = transactionCompare => {
    switch (transactionCompare.type) {
        case Type.sale_invoice:
        case Type.sale_credit_note:
        case Type.sale_delivery_order:
        case Type.sale_payment:
        case Type.sale_refund:
        case Type.sale_quote:
        case Type.sale_order:
        case Type.contra:
            return t`Customer`;
        case Type.purchase_bill:
        case Type.purchase_order:
        case Type.purchase_goods_received_note:
            return t`Supplier`;
        case Type.purchase_credit_note:
        case Type.purchase_payment:
        case Type.purchase_refund:
        case Type.bank_income:
        case Type.bank_expense:
        case Type.journal_entry:
        default:
            return t`Contact`;
    }
};

export const getNumberItemLabel = () => t`No.`;

export const getAccountItemLabel = transactionCompare => {
    switch (transactionCompare.type) {
        case Type.sale_refund:
        case Type.purchase_payment:
        case Type.bank_expense:
            return t`Pay From`;
        case Type.sale_payment:
        case Type.purchase_refund:
        case Type.bank_income:
            return t`Deposit To`;
        case Type.stock_adjustment:
            return t`Adjustment Account`;
        default:
            return t`Account`;
    }
};

export const getPaymentMethodLiteral = (payment_method_id, paymentMethodsList) => paymentMethodsList
    .find(method => method.id === payment_method_id)?.name;

export const getPaymentModeLiteral = (payment_mode, transaction_type) => {
    switch (payment_mode) {
        case 'cash':
            if (transaction_type === Type.sale_invoice)
                return t`Cash Sales`;
            if (transaction_type === Type.purchase_bill)
                return t`Cash Purchase`;
            break;
        case 'credit':
            if (transaction_type === Type.sale_invoice)
                return t`Credit Sales`;
            if (transaction_type === Type.purchase_bill)
                return t`Credit Purchase`;
            break;
        case 'claim':
            return t`Expense Claim`;
        default:
            return null;
    }
    return null;
};

// if before and after exist, it will always return "same" even when fields are changed
const getTransactionChangeStatus = audit => {
    // const { audit } = this.props;
    if (!audit.compare_data && !audit.current_data)
        return undefined;
    if (audit.compare_data && !audit.current_data)
        return "removed";
    if (!audit.compare_data && audit.current_data)
        return "added";
    if (audit.compare_data && audit.current_data)
        return "same";
    return null;
};

// To handle cases like DELETED, MARKED_VOID, DEACTIVATED
// where there's only compare_data OR current_data but not both
export const preprocessAudit = audit => {
    // extract relevant data from retrieved audit
    const processedTransactionCompare = {
        before: audit.compare_data,
        after: audit.current_data,
        type: audit.type,
        currency_code: audit.currency_code,
        action: audit.action,
    };
    switch (getTransactionChangeStatus(audit)) {
        case "added":
            processedTransactionCompare.before = processedTransactionCompare.after;
            return processedTransactionCompare;
        case "removed":
            processedTransactionCompare.after = processedTransactionCompare.before;
            return processedTransactionCompare;
        case "same":
            return processedTransactionCompare;
        case undefined:
            processedTransactionCompare.before = {};
            processedTransactionCompare.after = {};
            return processedTransactionCompare;
        default:
            return undefined;
    }
};
